import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef } from 'react';
import { useElementErrorMsg, useExtraTag, useFetch, useLabels } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ElementModality, ElementType } from '@/app/types';
import { convertViduV1ElementToElementType, reportEvent } from '@/app/utils';
import { materialsListPersonalElements } from '../apis';
import { ELEMENTS_PAGE_SIZE } from '../constants';
import { HEAD_TAB } from '../types';

interface UsePersonalElementsProps {
  refreshToken: string;
  activeHeadTab: HEAD_TAB;
  setElements: Dispatch<SetStateAction<ElementType[]>>;
  searchValue: string;
  elements: ElementType[];
  allowedModalities?: ElementModality[];
  updateRefreshToken: () => void;
  onItemChange?: (element: ElementType) => void;
  onDelete?: (ids: string[]) => void;
  setSelectedElements: Dispatch<SetStateAction<ElementType[]>>;
}

export function usePersonalElements(props: UsePersonalElementsProps) {
  const {
    refreshToken,
    activeHeadTab,
    setElements,
    searchValue,
    elements,
    allowedModalities,
    updateRefreshToken,
    onItemChange,
    onDelete,
    setSelectedElements,
  } = props;

  const { allTag } = useExtraTag();

  const tokenRef = useRef('');

  const { tags, selectedTags, onSelect: onSelectTag, getTags, onDelete: onDeleteTag, selectAll } = useLabels();
  const { showErrorMsg } = useElementErrorMsg();
  const { referenceModalUpdateReason } = useStore((state) => ({
    referenceModalUpdateReason: state.referenceModalUpdateReason,
  }));

  const updateToken = useCallback((token?: string) => {
    tokenRef.current = token || '';
  }, []);

  /**
   * 更新单个元素
   */
  const updateElement = useCallback(
    (element: ElementType) => {
      const isCurrentTagSubject =
        selectedTags?.includes(allTag.id) || selectedTags?.includes(element.labels?.[0]?.id ?? '');
      if (isCurrentTagSubject) {
        setElements((prevElements) => {
          const subjectExist = !!prevElements.filter((it) => it.id === element.id)?.length;
          if (subjectExist) {
            return prevElements.map((it) => {
              if (it.id === element.id) {
                return element;
              }
              return it;
            });
          }
          return [element, ...prevElements];
        });
      }
    },
    [selectedTags, allTag.id],
  );

  /**
   * 删除标签
   */
  const deleteTag = useCallback(
    (id: string) => {
      onDeleteTag(id);
      updateRefreshToken();
    },
    [onDeleteTag, updateRefreshToken],
  );

  const handleTagSelectorChange = useCallback(
    (id: string) => {
      reportEvent('element_selector_page_click', {
        click_position: 'tag',
      });
      onSelectTag(id);
    },
    [onSelectTag],
  );

  const elementsListReq = useFetch(
    () => {
      return materialsListPersonalElements({
        query: {
          labels: selectedTags?.length === 1 && selectedTags.includes(allTag.id) ? undefined : selectedTags,
          'pager.page_token': tokenRef.current ?? '',
          'pager.pagesz': ELEMENTS_PAGE_SIZE,
          ...(searchValue && {
            keyword: searchValue,
          }),
          ...(allowedModalities && {
            modalities: allowedModalities,
          }),
        },
      });
    },
    {
      manual: true,
      onSuccess: (data) => {
        if (activeHeadTab === HEAD_TAB.REFERENCE) {
          if (data.error) {
            showErrorMsg(data.error);
          } else {
            if (!tokenRef.current && searchValue) {
              reportEvent('search_result', {
                is_success: !!data?.data.elements?.length,
                position: 'create',
              });
            }
            setElements([
              ...(tokenRef.current ? elements : []),
              ...(data?.data.elements ?? []).map((el) => convertViduV1ElementToElementType(el)),
            ]);
            updateToken(data.data.next_page_token ?? '');
          }
        }
      },
    },
  );

  useEffect(() => {
    if (activeHeadTab === HEAD_TAB.REFERENCE) {
      updateToken('');
      setElements([]);
      elementsListReq.run();
    }
  }, [activeHeadTab, selectedTags, refreshToken, searchValue, allowedModalities]);

  useEffect(() => {
    if (!referenceModalUpdateReason) return;

    if (referenceModalUpdateReason.type === 'update') {
      const element = referenceModalUpdateReason.reference;
      if (element) {
        updateElement(element);
        onItemChange?.(element);
      }
      setSelectedElements([]);
      updateRefreshToken();

      getTags();
    } else if (referenceModalUpdateReason.type === 'delete') {
      const id = referenceModalUpdateReason?.reference?.id || '';
      updateRefreshToken();
      setSelectedElements((prev) => prev.filter((it) => it.id !== id));
      onDelete?.([id]);
    } else if (referenceModalUpdateReason.type === 'add') {
      updateRefreshToken();
    }
  }, [referenceModalUpdateReason]);

  return {
    tags,
    selectedTags,
    deleteTag,
    handleTagSelectorChange,
    elementsListReq,
    getTags,
    selectAll,
  };
}
